import { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { TypeAnimation } from 'react-type-animation';
import { Link } from 'react-scroll';

const featured = ['Rhythmiq', 'Safebox', 'ProspectIQ', 'Restaurant Website'];

export default function Hero() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const ctx = canvas.getContext('2d');
    const colors = ['54,234,211', '255,77,141', '255,209,102', '163,255,103'];
    let width = window.innerWidth;
    let height = Math.max(window.innerHeight * 0.92, 680);
    let raf;

    const resize = () => {
      width = canvas.width = window.innerWidth;
      height = canvas.height = Math.max(window.innerHeight * 0.92, 680);
    };

    const particles = Array.from({ length: 76 }, (_, index) => ({
      x: Math.random() * width,
      y: Math.random() * height,
      size: Math.random() * 1.8 + 0.7,
      speedX: (Math.random() - 0.5) * 0.34,
      speedY: (Math.random() - 0.5) * 0.34,
      color: colors[index % colors.length],
    }));

    const draw = () => {
      ctx.clearRect(0, 0, width, height);
      particles.forEach((p, index) => {
        p.x += p.speedX;
        p.y += p.speedY;

        if (p.x < 0) p.x = width;
        if (p.x > width) p.x = 0;
        if (p.y < 0) p.y = height;
        if (p.y > height) p.y = 0;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${p.color},0.7)`;
        ctx.fill();

        for (let j = index + 1; j < particles.length; j += 1) {
          const other = particles[j];
          const distance = Math.hypot(p.x - other.x, p.y - other.y);
          if (distance < 110) {
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(other.x, other.y);
            ctx.strokeStyle = `rgba(${p.color},${0.11 - distance / 1200})`;
            ctx.lineWidth = 1;
            ctx.stroke();
          }
        }
      });
      raf = requestAnimationFrame(draw);
    };

    resize();
    draw();
    window.addEventListener('resize', resize);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <section
      id="home"
      className="hero-section"
      style={{
        position: 'relative',
        minHeight: '92vh',
        padding: '142px 0 76px',
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
        background: 'var(--ink)',
      }}
    >
      <canvas ref={canvasRef} className="hero-canvas" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', opacity: 0.75 }} />

      <div
        aria-hidden="true"
        className="hero-backdrop"
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(90deg, rgba(7,7,8,0.94) 0%, rgba(7,7,8,0.82) 38%, rgba(7,7,8,0.34) 70%, rgba(7,7,8,0.08) 100%), linear-gradient(to bottom, rgba(7,7,8,0.1), var(--ink) 98%)',
        }}
      />

      <div
        aria-hidden="true"
        className="hero-glow"
        style={{
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          width: '52%',
          background:
            'linear-gradient(135deg, rgba(54,234,211,0.12), transparent 34%), linear-gradient(225deg, rgba(255,77,141,0.12), transparent 38%)',
          pointerEvents: 'none',
        }}
      />

      <motion.img
        src="/img/mypic.png"
        alt="Prakash C, web developer in Chennai and freelance app developer in India"
        className="hero-photo"
        loading="eager"
        fetchPriority="high"
        initial={{ opacity: 0, scale: 1.035, x: 28 }}
        animate={{ opacity: 0.92, scale: 1, x: 0 }}
        transition={{ duration: 1.1, delay: 0.15 }}
        style={{
          position: 'absolute',
          right: 'clamp(0px, 3vw, 64px)',
          bottom: 0,
          height: '90%',
          width: 'min(100vw, 1000px)',
          objectFit: 'cover',
          objectPosition: 'center top',
          filter: 'contrast(1.08) saturate(1.08) brightness(1.12)',
          maskImage: 'linear-gradient(90deg, transparent 0%, black 18%, black 88%, transparent 100%)',
          WebkitMaskImage: 'linear-gradient(90deg, transparent 0%, black 18%, black 88%, transparent 100%)',
        }}
      />

      <div className="section-shell" style={{ position: 'relative', zIndex: 2 }}>
        <motion.div
          className="hero-content"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          style={{ maxWidth: 620 }}
        >
          <div className="eyebrow">Web Developer in Chennai</div>
          <h1
            className="hero-title"
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'clamp(2rem, 3.8vw, 3.25rem)',
              fontWeight: 800,
              lineHeight: 1.14,
              letterSpacing: '-0.02em',
              color: 'var(--white)',
              marginBottom: 16,
            }}
          >
            <span
              style={{
                background: 'linear-gradient(135deg, var(--aqua), #e0f7fa 50%, var(--sun))',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                color: 'transparent',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Web and app development
            </span>
            <br />
            by{' '}
            <span
              style={{
                background: 'linear-gradient(90deg, var(--sun), var(--rose))',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                color: 'transparent',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Prakash C.
            </span>
          </h1>
          <div style={{ minHeight: 40, marginBottom: 22 }}>
            <TypeAnimation
              className="hero-role"
              sequence={[
                'Flutter App Developer India',
                1800,
                'React Web Developer',
                1800,
                '.NET Project Developer',
                1800,
                'Freelance Web Developer',
                1800,
              ]}
              wrapper="p"
              speed={48}
              repeat={Infinity}
              style={{
                color: 'var(--aqua)',
                fontFamily: 'var(--font-display)',
                fontWeight: 800,
                fontSize: 'clamp(1.1rem, 3vw, 1.65rem)',
                letterSpacing: 0,
              }}
            />
          </div>
          <p className="hero-copy" style={{ color: 'var(--soft)', fontSize: 18, maxWidth: 620, lineHeight: 1.75, marginBottom: 34 }}>
            I help clients in Chennai and across India launch fast, mobile-friendly websites, Flutter apps, React interfaces, and .NET web solutions with premium design and clean development.
          </p>

          <div className="hero-actions" style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 48 }}>
            <Link to="portfolio" href="#portfolio" spy smooth offset={-80} duration={600}>
              <button className="btn-primary">View Projects</button>
            </Link>
            <Link to="contact" href="#contact" spy smooth offset={-80} duration={600}>
              <button className="btn-secondary">Start a Project</button>
            </Link>
          </div>

          <div className="hero-stats" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 14, maxWidth: 650 }}>
            {[
              ['15+', 'Web projects'],
              ['4', 'Featured builds'],
              ['100%', 'Responsive focus'],
            ].map(([number, label], index) => (
              <motion.div
                key={label}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.65 + index * 0.08 }}
                className="premium-card"
                style={{ padding: '18px 20px', boxShadow: 'none', background: 'rgba(255,255,255,0.055)' }}
              >
                <div style={{ fontFamily: 'var(--font-display)', fontSize: 30, fontWeight: 800, color: index === 0 ? 'var(--sun)' : index === 1 ? 'var(--rose)' : 'var(--lime)' }}>
                  {number}
                </div>
                <div style={{ color: 'var(--muted)', fontSize: 12, letterSpacing: 1.2, textTransform: 'uppercase', fontFamily: 'var(--font-display)', fontWeight: 700 }}>
                  {label}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>

      <div
        className="hero-marquee"
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 3,
          borderTop: '1px solid rgba(255,255,255,0.12)',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          background: 'rgba(255,255,255,0.045)',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
        }}
      >
        <div style={{ display: 'inline-flex', gap: 34, padding: '13px 0', animation: 'marquee 20s linear infinite' }}>
          {[...featured, ...featured, ...featured, ...featured].map((item, index) => (
            <span
              key={`${item}-${index}`}
              style={{ fontFamily: 'var(--font-display)', fontSize: 12, fontWeight: 800, letterSpacing: 1.4, textTransform: 'uppercase', color: index % 2 ? 'var(--aqua)' : 'var(--sun)' }}
            >
              {item}
            </span>
          ))}
        </div>
      </div>

      <style>{`
        .hero-section {
          isolation: isolate;
        }

        .hero-section > .section-shell {
          flex: 0 0 min(1180px, calc(100% - 40px));
          max-width: 1180px;
          margin-left: auto;
          margin-right: auto;
        }

        .hero-title {
          text-wrap: balance;
          max-width: 760px;
        }

        .hero-actions > a {
          text-decoration: none;
        }

        @media (max-width: 1180px) {
          .hero-title {
            max-width: 680px !important;
          }

          .hero-photo {
            width: min(44vw, 500px) !important;
            right: 0 !important;
          }
        }

        @media (max-width: 900px) {
          .hero-section {
            min-height: auto !important;
            padding: 122px 0 72px !important;
            align-items: flex-start !important;
          }

          .hero-backdrop {
            background:
              linear-gradient(180deg, rgba(7,7,8,0.6) 0%, rgba(7,7,8,0.88) 48%, var(--ink) 100%),
              linear-gradient(90deg, rgba(7,7,8,0.9) 0%, rgba(7,7,8,0.5) 100%) !important;
          }

          .hero-glow {
            width: 100% !important;
            opacity: 0.58 !important;
          }

          .hero-photo {
            top: 70px !important;
            right: -8vw !important;
            bottom: auto !important;
            width: min(78vw, 520px) !important;
            height: 430px !important;
            opacity: 0.28 !important;
            object-position: center top !important;
            mask-image: linear-gradient(180deg, black 0%, black 56%, transparent 100%) !important;
            -webkit-mask-image: linear-gradient(180deg, black 0%, black 56%, transparent 100%) !important;
            filter: contrast(1.08) saturate(1.06) brightness(1.08) !important;
          }

          .hero-content {
            max-width: 620px !important;
          }

          .hero-title {
            max-width: 620px !important;
            font-size: clamp(2.5rem, 6.1vw, 3.45rem) !important;
            line-height: 1.02 !important;
          }

          .hero-stats { grid-template-columns: 1fr 1fr 1fr !important; max-width: 100% !important; }
        }

        @media (max-width: 680px) {
          .hero-section > .section-shell {
            flex-basis: calc(100% - 28px);
          }

          .hero-section {
            min-height: auto !important;
            padding: 106px 0 58px !important;
          }

          .hero-canvas {
            opacity: 0.42 !important;
          }

          .hero-photo {
            top: 76px !important;
            right: -26vw !important;
            width: 112vw !important;
            height: 390px !important;
            opacity: 0.2 !important;
          }

          .hero-content {
            max-width: 100% !important;
          }

          .hero-title {
            font-size: clamp(2.05rem, 9vw, 2.7rem) !important;
            line-height: 1.02 !important;
            margin-bottom: 16px !important;
          }

          .hero-role {
            font-size: 1.08rem !important;
          }

          .hero-content p {
            font-size: 15.5px !important;
            line-height: 1.7 !important;
            margin-bottom: 22px !important;
          }

          .hero-actions {
            display: grid !important;
            grid-template-columns: 1fr !important;
            gap: 12px !important;
            margin-bottom: 24px !important;
          }

          .hero-actions > a,
          .hero-actions button {
            width: 100% !important;
          }

          .hero-stats {
            grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
            gap: 8px !important;
            max-width: 100% !important;
          }

          .hero-stats .premium-card {
            min-width: 0 !important;
            padding: 10px 8px !important;
          }

          .hero-stats .premium-card > div:first-child {
            font-size: 22px !important;
          }

          .hero-stats .premium-card > div:last-child {
            font-size: 9px !important;
            line-height: 1.25 !important;
            letter-spacing: 0.7px !important;
          }

          .hero-marquee {
            position: relative !important;
            margin-top: 28px !important;
          }
        }

        @media (max-width: 380px) {
          .hero-section {
            padding-top: 100px !important;
          }

          .hero-title {
            font-size: 2rem !important;
          }

          .eyebrow {
            font-size: 10px !important;
            letter-spacing: 1.4px !important;
            gap: 9px !important;
          }

          .eyebrow::before {
            width: 24px !important;
          }
        }
      `}</style>
    </section>
  );
}
